import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import chroma from "chroma-js";
import flatten from "lodash/flatten";
import leafletStyles from "leaflet/dist/leaflet.css";
import Viewport from "views/logic/aggregationbuilder/visualizations/Viewport";
import style from "./MapVisualization.css";
import { useIsInteractiveMode } from "../../contexts/InteractiveContext";
const DEFAULT_VIEWPORT = Viewport.create([0, 0], 1);
const _getBucket = (value, bucketCount, minValue, maxValue, increment) => {
  const bucketSize = (maxValue - minValue) / bucketCount;
  const bucket = value < maxValue ? Math.ceil((value - minValue) / bucketSize) : bucketCount;
  return bucket + increment;
};
const Marker = ({ coordinates, value, min, max, radiusSize, increment, color, name, keys }) => {
  const formattedCoordinates = coordinates.split(",").map((component) => Number(component)).filter((n) => !isNaN(n));
  if (formattedCoordinates.length !== 2) {
    return null;
  }
  const radius = _getBucket(value, radiusSize, min, max, increment);
  const markerKeys = flatten(
    Object.entries(keys).map(([k, v]) => [
      /* @__PURE__ */ React.createElement("dt", { key: `dt-${k}-${v}` }, k),
      /* @__PURE__ */ React.createElement("dd", { key: `dd-${k}-${v}` }, v)
    ])
  );
  return /* @__PURE__ */ React.createElement(
    CircleMarker,
    {
      key: `${name}-${coordinates}`,
      center: formattedCoordinates,
      radius,
      color: color.hex(),
      fillColor: color.hex(),
      weight: 2,
      opacity: 0.8
    },
    /* @__PURE__ */ React.createElement(Popup, null, /* @__PURE__ */ React.createElement("dl", null, /* @__PURE__ */ React.createElement("dt", null, "Name"), /* @__PURE__ */ React.createElement("dd", null, name), markerKeys, /* @__PURE__ */ React.createElement("dt", null, "Coordinates:"), /* @__PURE__ */ React.createElement("dd", null, coordinates), value && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("dt", null, "Value:"), /* @__PURE__ */ React.createElement("dd", null, value))))
  );
};
const MapEvents = ({
  onViewportChanged
}) => {
  const map = useMap();
  const _onViewportChanged = () => {
    const { lat, lng } = map.getCenter();
    return onViewportChanged(
      Viewport.create([Number.parseFloat(lat.toFixed(4)), Number.parseFloat(lng.toFixed(4))], map.getZoom())
    );
  };
  useMapEvents({
    dragend: _onViewportChanged,
    zoomend: _onViewportChanged
  });
  return null;
};
const defaultOnRenderComplete = () => {
};
const MapVisualization = ({
  attribution = '&copy; <a href="http://osm.org/copyright" target="_blank">OpenStreetMap</a> contributors',
  data,
  height,
  id,
  locked = false,
  markerRadiusIncrementSize = 10,
  markerRadiusSize = 10,
  onChange,
  onRenderComplete = defaultOnRenderComplete,
  url = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  viewport = DEFAULT_VIEWPORT,
  width
}) => {
  const [_isMapReady, setIsMapReady] = useState(false);
  const [_areTilesReady, setAreTilesReady] = useState(false);
  const [_viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const _map = useRef();
  useEffect(() => {
    leafletStyles.use();
    return leafletStyles.unuse;
  }, []);
  useEffect(() => {
    if (_viewport && (_map == null ? void 0 : _map.current)) {
      if (viewport.center !== _viewport.center || viewport.zoom !== _viewport.zoom) {
        _map.current.setView([viewport.center[0], viewport.center[1]], viewport.zoom);
      }
    }
  }, [viewport.center, viewport.zoom]);
  useEffect(() => {
    if (_areTilesReady && _isMapReady) {
      onRenderComplete();
    }
  }, [_areTilesReady, _isMapReady, onRenderComplete]);
  const _handleMapReady = useCallback(() => {
    setIsMapReady(true);
  }, []);
  const _handleTilesReady = useCallback(() => {
    setAreTilesReady(true);
  }, []);
  const _onChange = useCallback(
    (newViewport) => {
      setViewport(newViewport);
      return onChange(newViewport);
    },
    [onChange]
  );
  const noOfKeys = data.length;
  const chromaScale = chroma.scale("Spectral");
  const markers = data.flatMap(({ keys, name, values }, idx) => {
    const y = Object.values(values);
    const min = Math.min(...y);
    const max = Math.max(...y);
    const color = chromaScale(idx * (1 / noOfKeys));
    return Object.entries(values).map(([coord, value], valueIdx) => /* @__PURE__ */ React.createElement(
      Marker,
      {
        key: `${name}-${coord}-${value}`,
        coordinates: coord,
        value,
        min,
        max,
        radiusSize: markerRadiusSize,
        increment: markerRadiusIncrementSize,
        color,
        name,
        keys: keys[valueIdx]
      }
    ));
  });
  const interactive = useIsInteractiveMode();
  return /* @__PURE__ */ React.createElement("div", { className: locked ? style.mapLocked : "", style: { position: "relative", zIndex: 0 } }, locked && /* @__PURE__ */ React.createElement("div", { className: style.overlay, style: { height, width } }), /* @__PURE__ */ React.createElement(
    MapContainer,
    {
      ref: _map,
      boundsOptions: { maxZoom: 19, animate: interactive },
      center: viewport.center,
      className: style.map,
      closePopupOnClick: interactive,
      doubleClickZoom: interactive,
      dragging: interactive,
      fadeAnimation: interactive,
      id: `visualization-${id}`,
      key: `visualization-${id}-${width}-${height}`,
      markerZoomAnimation: interactive,
      scrollWheelZoom: interactive,
      style: { height, width },
      touchZoom: interactive,
      trackResize: interactive,
      whenReady: _handleMapReady,
      zoom: viewport.zoom,
      zoomAnimation: interactive,
      zoomControl: interactive
    },
    /* @__PURE__ */ React.createElement(MapEvents, { onViewportChanged: _onChange }),
    /* @__PURE__ */ React.createElement(TileLayer, { url, attribution, eventHandlers: { load: _handleTilesReady } }),
    markers
  ));
};
export default MapVisualization;
